import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type { FormEvent, ReactElement } from 'react';
import api from '@/api';
import { ArchiveSidebar } from '@/archive/ArchiveSidebar';
import { nextArchivePage, PAGE_SIZE, PostArchive } from '@/archive/PostArchive';
import { apiLanguage } from '~/i18n';
import { localeToApiLanguage } from '~/utils/languageFormat';

/**
 * The Study Helper Tutorials archive.
 *
 * Ordered by `menu_order`, as legacy orders tutorials; this is the only kind
 * where that column carries real data. Legacy's order within a tie wasn't
 * stable, so the server's `published_at, id` tie-break is ours, and nothing
 * should assert an order inside a `menu_order` group.
 */
function StudyHelperTutorialsArchive(): ReactElement {
  const navigate = useNavigate();
  const { search_term } = Route.useSearch();
  // The interface language, which is part of the query key. Unlike legacy
  // there's no language prefix in the URL.
  const language = localeToApiLanguage(apiLanguage());

  const query = api.listStudyHelperTutorials.useInfiniteQuery(
    { query: { language, search: search_term || undefined, limit: PAGE_SIZE } },
    {
      initialPageParam: { query: { offset: 0 } },
      // Wrapped rather than passed by reference: an annotated `lastPage`
      // would be inferred as the operation's `TQueryFnData` and collapse the
      // page type to the helper's structural one.
      getNextPageParam: (lastPage, allPages) =>
        nextArchivePage(lastPage, allPages),
    },
  );

  const handleSearch = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const term = new FormData(e.currentTarget).get('search_term');
    void navigate({
      to: '/study-helper-tutorials/',
      search: { search_term: term ? `${term}` : undefined },
    });
  };

  return (
    <PostArchive
      query={query}
      basePath="/study-helper-tutorials"
      emptyTestId="study-helper-tutorials-empty"
      searchTerm={search_term}
      onSearch={handleSearch}
      // Two category sections where legacy renders four: legacy's other two
      // headings came from stale WordPress term counts and always had empty
      // lists, so counting the real posts drops them.
      sidebar={
        <ArchiveSidebar
          sidebar="study-helper-tutorials-sidebar"
          language={language}
          postTo="/study-helper-tutorials/$slug"
          searchTerm={search_term}
          onSearch={handleSearch}
        />
      }
    />
  );
}

export const Route = createFileRoute('/_app/study-helper-tutorials/')({
  component: StudyHelperTutorialsArchive,
  validateSearch: (
    search: Record<string, unknown>,
  ): { search_term?: string } => ({
    search_term: search.search_term ? `${search.search_term}` : undefined,
  }),
});
